// @owners { team: patients }
import {
  ActionSheetContext,
  Body,
  Button,
  Column,
  Description,
  ExternalLink,
  InlineAlert,
  InputCheckbox,
  InputPassword,
  InputText,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { signUp } from '~shared/actions/auth';
import { setOnboardingVerificationUser } from '~shared/actions/verifications';
import {
  DEFAULT_ERROR_ALERT_MESSAGE,
  PASSWORD_ERROR_MESSAGE,
  PHARMACY_NOTICES_URL,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
  getUnderAgeEighteenErrorMessage,
} from '~shared/constants';
import { formatDateInput, isUnderAgeEighteen } from '~shared/helpers/date';
import { formatPhone } from '~shared/helpers/helper';
import { mapControllerToInputProps } from '~shared/helpers/mapFieldToInputProps';
import { usePageViewedEvent } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { validateDateOfBirth, validateEmail, validatePassword } from '~shared/validations';
import { VerificationActionSheet } from './VerificationActionSheet';
import { getSignUpUser } from '~web/features/onboarding/helpers/utils';

export type SignUpFormValues = {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone: string;
  email: string;
  password: string;
  terms: boolean;
  referral_code: string;
};

const DATE_REG_EXP = /((0[1-9]|1[0-2]) \/ (0[1-9]|[12]\d|3[01]) \/ [12]\d{3})/;

export const SignupForm = () => {
  const { navigate } = useNavigation();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const dispatch = useDispatchShared();
  const loading = useSelectorShared((state) => state.auth.signingUp);
  const authError = useSelectorShared((state) => state.auth.authError);
  const isFertDeliverySignup = useSelectorShared((state) => state.uiOnboarding.isFertDeliverySignup);
  const errorMessage = authError?.details?.message;

  usePageViewedEvent({
    event: EVENTS.SIGNUP_CREATE_ACCOUNT_VIEWED,
  });

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      phone: '',
      email: '',
      password: '',
      terms: false,
      referral_code: '',
    },
    mode: 'onBlur',
  });

  const firstNameController = useController({
    control,
    name: 'first_name',
    rules: { required: 'First name cannot be blank' },
  });

  const lastNameController = useController({
    control,
    name: 'last_name',
    rules: { required: 'Last name cannot be blank' },
  });

  const dateOfBirthController = useController({
    control,
    name: 'date_of_birth',
    rules: {
      required: 'Date of birth cannot be blank',
      validate: (dateOfBirth: string) => {
        if (!DATE_REG_EXP.test(dateOfBirth)) return 'Birthdate should be in format MM / DD / YYYY';
        const validationError = validateDateOfBirth(dateOfBirth, 'MM / dd / yyyy');
        if (validationError) return validationError;
        if (isUnderAgeEighteen(dateOfBirth)) return getUnderAgeEighteenErrorMessage();
        return true;
      },
    },
  });

  const phoneController = useController({
    control,
    name: 'phone',
    rules: { required: 'Phone cannot be blank' },
  });

  const emailController = useController({
    control,
    name: 'email',
    rules: { required: 'Email cannot be blank', validate: validateEmail },
  });

  const passwordController = useController({
    control,
    name: 'password',
    rules: { required: 'Password cannot be blank', validate: validatePassword },
  });

  const termsController = useController({
    control,
    name: 'terms',
    rules: { required: 'Please accept the terms and conditions' },
  });

  const handleCreateAccount = async (values: SignUpFormValues) => {
    const user = getSignUpUser(values);
    // eslint-disable-next-line import/no-deprecated
    const { success, verificationRequired } = await dispatch(signUp(user, {}, isFertDeliverySignup));
    if (success) {
      navigate('RouteAppTabNavigator', { screen: 'RouteTabHomescreen' });
    } else if (verificationRequired) {
      dispatch(setOnboardingVerificationUser(user));
      setActiveActionSheet(<VerificationActionSheet />);
    }
  };

  return (
    <Column gap="SM">
      {errorMessage ? (
        <InlineAlert type="warning">
          <Body>
            {errorMessage === DEFAULT_ERROR_ALERT_MESSAGE ? (
              <>
                Account creation failed. If you already have an account try{' '}
                <ExternalLink href="/">logging in here</ExternalLink>. If the problem persists please contact support.
              </>
            ) : (
              errorMessage
            )}
          </Body>
        </InlineAlert>
      ) : null}
      <InputText
        label="First name"
        accessibilityLabel="First name"
        placeholder="First name"
        textContentType="name"
        required
        {...mapControllerToInputProps(firstNameController)}
      />
      <InputText
        label="Last name"
        accessibilityLabel="Last name"
        placeholder="Last name"
        required
        textContentType="familyName"
        {...mapControllerToInputProps(lastNameController)}
      />
      <InputText
        label="Date of birth"
        accessibilityLabel="Date of birth"
        placeholder="MM / DD / YYYY"
        required
        caption={
          dateOfBirthController.fieldState.error?.message !== getUnderAgeEighteenErrorMessage()
            ? getUnderAgeEighteenErrorMessage()
            : undefined
        }
        textContentType="birthdate"
        {...mapControllerToInputProps(dateOfBirthController)}
        onChangeText={(value) => {
          dateOfBirthController.field.onChange(formatDateInput(value));
        }}
      />
      <InputText
        label="Mobile phone"
        accessibilityLabel="Mobile phone"
        placeholder="555-555-5555"
        required
        {...mapControllerToInputProps(phoneController)}
        onChangeText={(value) => {
          phoneController.field.onChange(formatPhone(value));
        }}
      />
      <InputText
        label="Email"
        accessibilityLabel="Email"
        required
        placeholder="name@email.com"
        {...mapControllerToInputProps(emailController)}
      />
      <InputPassword
        label="Create password"
        accessibilityLabel="Create password"
        caption={PASSWORD_ERROR_MESSAGE}
        placeholder="Password"
        onChangeText={(value) => {
          passwordController.field.onChange(value);
        }}
        {...mapControllerToInputProps(passwordController)}
      />
      <InputCheckbox
        noHighlight
        label=""
        fullBleed
        descriptions={
          <Description>
            I have read and accept the{' '}
            <ExternalLink
              textSize="mini"
              href={TERMS_OF_SERVICE_URL}
            >
              Terms of Service
            </ExternalLink>
            ,{' '}
            <ExternalLink
              textSize="mini"
              href={PRIVACY_POLICY_URL}
            >
              Privacy Policy
            </ExternalLink>
            , and{' '}
            <ExternalLink
              textSize="mini"
              href={PHARMACY_NOTICES_URL}
            >
              Pharmacy Notices
            </ExternalLink>
          </Description>
        }
        checkPlacement="left"
        {...mapControllerToInputProps(termsController)}
        onPress={() => {
          termsController.field.onChange(!termsController.field.value);
        }}
      />
      <Button
        label="Create account"
        disabled={!isValid || loading || !isDirty}
        loading={loading}
        loadingLabel="Creating account..."
        onPress={handleSubmit(handleCreateAccount)}
      />
    </Column>
  );
};
